import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import type { Ref } from 'vue';
import { computed } from 'vue';
import { betBuilderToMarketType } from '@/core/lib/betBuilderTypeConverter';
import { i18n } from '@/core/lib/i18n';
import { orderGroupedMarkets } from '@/core/oddsApi/helpers/formatBetBuilder';
import type { IBetBuilderMarket, IBetBuilderMultipleBetTypeColumn, IBetBuilderMultipleBetTypeConfig, IBetBuilderMultipleBetTypeGroup, IBetBuilderSingleBetTypeGroup } from '@/interface/betBuilder';
import { BetBuilderMarket, BetBuilderMultipleGroup, BetBuilderStatType } from '@/interface/enum';

const totalCornerMarketOrder = [
    BetBuilderMarket.SetTotalCornersHandicap,
    BetBuilderMarket.SetTotalCornersOverUnder,
    BetBuilderMarket.SetTotalCornersOddEven,
    BetBuilderMarket.SetTotalCorners1x2,
];

const totalCornerRowLabelKeys = ['odds_option_home', 'odds_option_away'];
const multipleBetTypeConfigs: IBetBuilderMultipleBetTypeConfig[] = [
    {
        key: 'totalCornerFullTime',
        title: 'Total Corners',
        titlePrefixKey: 'odds_header_ft',
        type: BetBuilderMultipleGroup.TotalCorner,
        marketOrder: totalCornerMarketOrder,
        statType: BetBuilderStatType.FullTimeCorners,
        rowLabelKeys: totalCornerRowLabelKeys,
    },
    {
        key: 'totalCornerFirstHalf',
        title: 'Total Corners',
        titlePrefixKey: 'odds_header_ht',
        type: BetBuilderMultipleGroup.TotalCorner,
        marketOrder: totalCornerMarketOrder,
        statType: BetBuilderStatType.FirstHalfCorners,
        rowLabelKeys: totalCornerRowLabelKeys,
    },
];

function matchesConfig(config: IBetBuilderMultipleBetTypeConfig, market: IBetBuilderMarket): boolean {
    return config.statType === market.matchStatType.id
        && config.marketOrder.includes(market.marketType.id);
}

function isMultipleBetTypeMarket(market: IBetBuilderMarket): boolean {
    return multipleBetTypeConfigs.some(config => matchesConfig(config, market));
}

function getColumnLabel(market: IBetBuilderMarket): string {
    const marketType = betBuilderToMarketType(market.marketType.id, market.matchStatType.id);

    return i18n.t(`market_type_short_name_${marketType}`);
}

function buildColumns(config: IBetBuilderMultipleBetTypeConfig, markets: IBetBuilderMarket[]): IBetBuilderMultipleBetTypeColumn[] {
    const grouped = groupBy(markets, market => `${market.matchStatType.id}_${market.marketType.id}`);

    const columns = Object.entries(grouped).map(([key, columnMarkets]) => {
        const [first] = columnMarkets;

        return {
            key,
            label: getColumnLabel(first),
            markets: columnMarkets,
            marketOrder: config.marketOrder.indexOf(first.marketType.id),
        };
    });

    return orderBy(columns, column => column.marketOrder)
        .map(({ key, label, markets: columnMarkets }) => ({ key, label, markets: columnMarkets }));
}

function buildRowLabels(config: IBetBuilderMultipleBetTypeConfig): string[] {
    return config.rowLabelKeys.map(key => i18n.t(key));
}

export const useBetBuilderMoreMarket = (markets: Ref<IBetBuilderMarket[]>) => {
    const singleBetTypeMarkets = computed(() => markets.value.filter(market => !isMultipleBetTypeMarket(market)));

    const multipleBetTypeMarkets = computed(() => markets.value.filter(market => isMultipleBetTypeMarket(market)));

    const singleBetTypeGroups = computed<IBetBuilderSingleBetTypeGroup[]>(() => {
        const grouped = groupBy(singleBetTypeMarkets.value, market => `${market.marketType.name}_${market.marketType.id}_${market.matchStatType.id}`);
        const pairs = Object.entries(grouped).map(([key, value]) => ({ key, value }));
        return orderGroupedMarkets(pairs).map(({ key, value }) => ({ key, markets: value }));
    });

    const multipleBetTypeGroups = computed<IBetBuilderMultipleBetTypeGroup[]>(() => multipleBetTypeConfigs.flatMap((config) => {
        const targetMarkets = multipleBetTypeMarkets.value.filter(market => matchesConfig(config, market));
        if (targetMarkets.length === 0) {
            return [];
        }

        return [{
            key: config.key,
            title: config.title,
            titlePrefixKey: config.titlePrefixKey,
            type: config.type,
            columns: buildColumns(config, targetMarkets),
            rowLabels: buildRowLabels(config),
        }];
    }));

    return {
        singleBetTypeGroups,
        multipleBetTypeGroups,
    };
};
